import { useEffect, useState, type FormEvent } from 'react';
import { MIN_PASSWORD, useAuth } from '../../app/AuthProvider';
import { supabase } from '../../data/supabase';
import { AuthCard, PasswordField } from './AuthCard';
import { Button, ErrorNote } from '../../ui/components';

type Mode = 'signin' | 'signup' | 'forgot';

/** Which domains may self-register (D82); the database enforces the same list. */
function useAllowedDomains(): string[] | null {
  const [domains, setDomains] = useState<string[] | null>(null);
  useEffect(() => {
    let cancelled = false;
    supabase
      .from('allowed_email_domains')
      .select('domain')
      .then(({ data }) => {
        if (!cancelled) setDomains((data ?? []).map((d) => d.domain));
      });
    return () => {
      cancelled = true;
    };
  }, []);
  return domains;
}

/** Sign in, create an account, or ask for a reset link (spec 6.5, V2 D82). */
export function SignInPage() {
  const { signIn, signUp, sendPasswordReset } = useAuth();
  const domains = useAllowedDomains();
  const [mode, setMode] = useState<Mode>('signin');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const domainHint = domains && domains.length > 0 ? domains.map((d) => '@' + d).join(' or ') : null;
  const emailDomain = email.includes('@') ? email.split('@')[1]!.trim().toLowerCase() : '';
  const domainLooksWrong = mode === 'signup' && !!emailDomain && !!domains && domains.length > 0 && !domains.includes(emailDomain);

  const switchTo = (m: Mode) => {
    setMode(m);
    setError(null);
    setNotice(null);
    setPassword('');
    setConfirm('');
  };

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setNotice(null);

    if (mode === 'signup') {
      if (password.length < MIN_PASSWORD) return setError(`Use at least ${MIN_PASSWORD} characters.`);
      if (password !== confirm) return setError('The two passwords do not match.');
    }

    setBusy(true);
    try {
      if (mode === 'signin') {
        const err = await signIn(email, password);
        // A wrong password and an unknown account look identical on purpose.
        if (err) setError(/invalid login/i.test(err) ? 'That email and password do not match an account.' : err);
      } else if (mode === 'signup') {
        const err = await signUp(email, password);
        if (err) setError(err);
      } else {
        const err = await sendPasswordReset(email);
        if (err) setError(err);
        // Never reveal whether an address has an account.
        else setNotice(`If ${email.trim()} has an account, a reset link is on its way.`);
      }
    } finally {
      setBusy(false);
    }
  };

  const title = mode === 'signin' ? 'Sign in' : mode === 'signup' ? 'Create your account' : 'Reset your password';

  return (
    <AuthCard title={title}>
      <form onSubmit={(e) => void submit(e)} className="space-y-3">
        <div>
          <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-ink-muted" htmlFor="email">Email</label>
          <input
            id="email"
            type="email"
            required
            autoFocus
            autoComplete="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder={domains && domains.length > 0 ? `you@${domains[0]}` : 'you@company.com'}
            className="w-full rounded-md border border-line px-3 py-2 text-sm focus:border-navy focus:outline-none"
          />
          {mode === 'signup' && domainHint && (
            <p className={`mt-1 text-xs ${domainLooksWrong ? 'text-danger' : 'text-ink-muted'}`}>
              Use your {domainHint} address.
            </p>
          )}
        </div>

        {mode !== 'forgot' && (
          <PasswordField
            id="password"
            label="Password"
            value={password}
            onChange={setPassword}
            autoComplete={mode === 'signup' ? 'new-password' : 'current-password'}
            hint={mode === 'signup' ? `At least ${MIN_PASSWORD} characters.` : undefined}
          />
        )}

        {mode === 'signup' && (
          <PasswordField
            id="confirm"
            label="Confirm password"
            value={confirm}
            onChange={setConfirm}
            autoComplete="new-password"
            error={confirm.length > 0 && confirm !== password ? 'The two passwords do not match.' : undefined}
          />
        )}

        {error && <ErrorNote message={error} />}
        {notice && <div className="rounded-md border border-ok-soft bg-ok-soft px-3 py-2 text-sm text-ok">{notice}</div>}

        <Button type="submit" variant="primary" busy={busy} className="w-full justify-center py-2" disabled={!email || (mode !== 'forgot' && !password)}>
          {mode === 'signin' ? 'Sign in' : mode === 'signup' ? 'Create account' : 'Email me a reset link'}
        </Button>
      </form>

      <div className="mt-4 flex flex-wrap items-center justify-between gap-2 text-sm">
        {mode === 'signin' ? (
          <>
            <button type="button" className="text-navy underline" onClick={() => switchTo('signup')}>Create an account</button>
            <button type="button" className="text-navy underline" onClick={() => switchTo('forgot')}>Forgot password?</button>
          </>
        ) : (
          <button type="button" className="text-navy underline" onClick={() => switchTo('signin')}>Back to sign in</button>
        )}
      </div>
    </AuthCard>
  );
}
